import { useContext, useEffect, useRef, useState } from "react";
import { AppLayout } from "../../../containers/layout/app";
import { GroupReportWrapper } from "./styled";
import { AppContext } from "../../../context/appContext";
import {
	Box,
	CircularProgress,
	Grid2,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Stack,
	Typography,
} from "@mui/material";
import { GroupReportTable } from "../../../containers/tables/groupreporttable";
import { useQuery } from "@tanstack/react-query";
import Cookies from "universal-cookie";
import { retrieveAllGroupsService } from "../../../utils/api/customerservice/grouplist/retrieveAllGroups";
import { BaseButton } from "../../../components/button/styled";
import { downloadGroupReportService } from "../../../utils/api/customerservice/grouplist/downloadGroupReport";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { BaseDropDownType } from "../../../types/component.type";

export const GroupReport = () => {
	const downloadMenuItems = [
		{
			title: "PDF",
			url: "pdf",
		},
		{
			title: "Excel",
			url: "excel",
		},
	];

	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const { setActiveNavItem } = useContext(AppContext);
	const dropdownRef = useRef<HTMLUListElement | null>(null);

	const [isDownloading, setIsDownloading] = useState(false);
	const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);
	const [isFetching, setIsFetching] = useState(false);
	const [paginationIndex, setPaginationIndex] = useState({
		page: "1",
		perPage: "10",
		totalPages: "1",
	});

	useEffect(() => {
		setActiveNavItem("Report");
	}, [setActiveNavItem]);

	const { data: verifiedGroups } = useQuery({
		queryKey: ["groups", TOKEN, paginationIndex],
		queryFn: async () => {
			setIsFetching(true);
			const response = await retrieveAllGroupsService(TOKEN, paginationIndex);
			const groups = response.data;
			if (!groups || groups.length === 0) return [];
			if (!Array.isArray(response) && response?.meta) {
				setPaginationIndex((prev) => ({
					...prev,
					totalPages: String(response?.meta?.totalPages ?? "1"),
				}));
			}
			setIsFetching(false);
			return groups;
		},
		enabled: !!TOKEN,
	});

	const handleDownloadGroupReport = async (
		e: React.MouseEvent<HTMLLIElement, MouseEvent>,
		type: string,
	) => {
		e.preventDefault();
		setIsDownloading(true);
		try {
			const blob = await downloadGroupReportService(TOKEN, type);
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = type === "pdf" ? "group_report.pdf" : "group_report.xlsx";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error: any) {
			console.error("Group report download failed:", error);
		} finally {
			setIsDownloading(false);
		}
	};

	const handleOpenDownloadMenuClick = () => {
		if (isDownloading) return;
		return setIsDownloadMenuOpen(true);
	};

	const handleDropDownClickOutside = (event: MouseEvent) => {
		if (
			dropdownRef.current &&
			!dropdownRef.current.contains(event.target as Node)
		) {
			return setIsDownloadMenuOpen(false);
		}
	};

	const handleDownloadMenuItemClick = async (
		e: React.MouseEvent<HTMLLIElement, MouseEvent>,
		item: BaseDropDownType,
	) => {
		e.preventDefault();
		switch (item.title) {
			case "PDF":
			case "Excel":
				await handleDownloadGroupReport(e, item.url);
				break;
			default:
				break;
		}
		return setIsDownloadMenuOpen(false);
	};

	const handlePagination = (
		e: React.MouseEvent<HTMLButtonElement>,
		type: "previous" | "next",
	) => {
		e.preventDefault();
		setPaginationIndex((prev) => {
			const currentPage = parseInt(prev.page, 10);
			const newPage =
				type === "previous"
					? Math.max(1, currentPage - 1)
					: Math.min(parseInt(prev.totalPages, 10), currentPage + 1);
			return {
				...prev,
				page: newPage.toString(),
			};
		});
	};

	useEffect(() => {
		if (isDownloadMenuOpen) {
			document.addEventListener("mousedown", handleDropDownClickOutside);
		} else {
			document.removeEventListener("mousedown", handleDropDownClickOutside);
		}
		return () => {
			document.removeEventListener("mousedown", handleDropDownClickOutside);
		};
	}, [isDownloadMenuOpen]);

	return (
		<AppLayout pageId={"Group-Report"} pageTitle={"Group Report"}>
			<GroupReportWrapper>
				<Box component={"div"} className="group-report-table-box">
					<GroupReportTable rows={verifiedGroups as Record<string, any>[]} />
				</Box>
				<Grid2 container component={"div"} spacing={"var(--flex-gap)"}>
					<Grid2
						size={{ mobile: 12, laptop: 8 }}
						display={{ mobile: "none", laptop: "grid" }}
					></Grid2>
					<Grid2 size={{ mobile: 12, laptop: 4 }}>
						<Stack
							direction={"row"}
							overflow={"hidden"}
							borderRadius={"6px"}
							alignItems={"center"}
							justifyContent={"space-around"}
							border={"1px solid var(--input-field-border-color)"}
						>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									disabled={
										parseInt(paginationIndex.page, 10) === 1 || isFetching
									}
									onClick={(e) => handlePagination(e, "previous")}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Previous
									</Typography>
								</BaseButton>
							</Box>
							<Box
								display={"flex"}
								overflow={"hidden"}
								bgcolor={"var(--primary-color)"}
								padding={"calc(var(--basic-padding)/2)"}
							>
								<Typography
									variant="h3"
									fontFamily={"Roboto"}
									fontWeight={600}
									fontSize={10}
									lineHeight={"normal"}
									color="var(--light-color)"
								>
									{`${paginationIndex.page} / ${paginationIndex.totalPages}`}
								</Typography>
							</Box>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									onClick={(e) => handlePagination(e, "next")}
									disabled={
										paginationIndex.page === paginationIndex.totalPages ||
										isFetching
									}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Next
									</Typography>
								</BaseButton>
							</Box>
						</Stack>
					</Grid2>
				</Grid2>
				<Stack className="call-to-action-stack">
					<Box>
						<BaseButton
							variant="contained"
							endIcon={<KeyboardArrowDownIcon />}
							padding="calc(var(--basic-padding)/4) calc(var(--basic-padding))"
							sx={{
								width: { mobile: "-webkit-fill-available", miniTablet: "auto" },
							}}
							onClick={handleOpenDownloadMenuClick}
						>
							{isDownloading ? (
								<CircularProgress color="inherit" className="loader" />
							) : (
								<Typography
									variant={"button"}
									fontFamily={"inherit"}
									fontWeight={"inherit"}
									fontSize={"inherit"}
									lineHeight={"inherit"}
									color={"inherit"}
									textTransform={"inherit"}
								>
									Download
								</Typography>
							)}
						</BaseButton>
					</Box>
					{isDownloadMenuOpen && (
						<List component={"ul"} ref={dropdownRef} className="dropdown-modal">
							{downloadMenuItems.map((item, index) => (
								<ListItem
									key={index}
									onClick={(e) => handleDownloadMenuItemClick(e, item)}
								>
									<ListItemButton>
										<ListItemText primary={item.title} />
									</ListItemButton>
								</ListItem>
							))}
						</List>
					)}
				</Stack>
			</GroupReportWrapper>
		</AppLayout>
	);
};
